import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import MasterCrud from "@/components/masters/MasterCrud";
import { Button } from "@/components/ui/button";

const CONFIG = {
  customers: {
    label: "Pelanggan", desc: "Kelola data pelanggan perusahaan, termasuk termin dan batas kredit.",
    cols: ["code", "name", "category", "city", "payment_term", "credit_limit"],
    tooltips: {
      credit_limit: "Batas maksimum piutang yang diperbolehkan untuk pelanggan ini.",
      unlimited_credit: "Nonaktifkan batas kredit untuk pelanggan ini.",
      payment_term: "Digunakan untuk menghitung jatuh tempo invoice pada modul Penjualan.",
    },
    sections: [
      { title: "Informasi Umum", fields: ["code","name","company_name","category","notes"] },
      { title: "Alamat & Kontak", fields: ["address","province","city","district","postal_code","pic","phone","whatsapp","email"] },
      { title: "Keuangan", fields: ["payment_term","payment_term_days","credit_limit","unlimited_credit","currency"] },
      { title: "Perpajakan", fields: ["npwp","nik","pkp_status","tax_status"] },
    ],
  },
  suppliers: {
    label: "Pemasok", desc: "Kelola data pemasok, mitra manufaktur, dan penyedia jasa.",
    cols: ["code", "name", "category", "city", "payment_term"],
    tooltips: {
      payment_term: "Termin pembayaran default kepada pemasok ini.",
      bank_account_number: "Nomor rekening pemasok. Data sensitif, akan ditampilkan bertopeng untuk sebagian peran.",
    },
    sections: [
      { title: "Informasi Umum", fields: ["code","name","company_name","category","notes"] },
      { title: "Alamat & Kontak", fields: ["address","province","city","pic","phone","whatsapp","email"] },
      { title: "Perbankan", fields: ["bank_name","bank_account_number","bank_account_holder"] },
      { title: "Keuangan & Perpajakan", fields: ["payment_term","payment_term_days","currency","npwp","nik","pkp_status"] },
    ],
  },
  products: {
    label: "Produk & SKU", desc: "Master produk dengan SKU, kategori, harga, dan pemasok.",
    cols: ["sku","name","category","default_sell_price","default_buy_price"],
    tooltips: {
      sku: "Kode unik produk. Wajib unik dalam sistem.",
      hpp_reference: "Nilai referensi harga pokok produk. Perhitungan HPP transaksi akan dikembangkan pada tahap berikutnya.",
      barcode: "Barcode produk (opsional). Bila diisi harus unik.",
    },
    sections: [
      { title: "Informasi Produk", fields: ["sku","barcode","brand","name","variant","category","notes"] },
      { title: "Satuan & Berat", fields: ["sales_unit","weight_unit","net_weight","qty_per_carton"] },
      { title: "Harga", fields: ["default_buy_price","hpp_reference","default_sell_price","currency"] },
      { title: "Pemasok", fields: ["supplier_code"] },
      { title: "Perpajakan", fields: ["tax_code","ppn_status"] },
      { title: "Lainnya", fields: ["photo_url"] },
    ],
  },
  product_categories: { label: "Kategori Produk", desc: "Kelompokkan produk berdasarkan kategori.", cols: ["code","name","description"] },
  units: { label: "Satuan", desc: "Satuan penjualan dan berat produk.", cols: ["code","name","description"] },
  warehouses: {
    label: "Gudang", desc: "Kelola lokasi gudang perusahaan dan mitra.",
    cols: ["code","name","warehouse_type","pic","phone"],
  },
  bank_accounts: {
    label: "Rekening Bank", desc: "Rekening bank operasional perusahaan.",
    cols: ["code","bank_name","account_number","currency","opening_balance"],
    tooltips: {
      opening_balance: "Saldo pembukuan awal rekening ini pada tanggal migrasi sistem.",
      coa_code: "Akun COA yang terkait dengan rekening ini.",
    },
  },
  cash_accounts: {
    label: "Kas", desc: "Kas utama, kas kecil, dan kas operasional.",
    cols: ["code","name","coa_code","opening_balance","pic"],
  },
  currencies: {
    label: "Mata Uang", desc: "Daftar mata uang yang digunakan sistem. Hanya satu mata uang dasar.",
    cols: ["code","name","symbol","decimal_places","is_base"],
    tooltips: { is_base: "Hanya boleh ada satu mata uang dasar. Default: IDR." },
  },
  exchange_rates: {
    label: "Kurs Mata Uang", desc: "Riwayat kurs per tanggal berlaku. Data historis tidak ditimpa.",
    cols: ["effective_date","currency","buy_rate","sell_rate","mid_rate","source"],
    tooltips: { effective_date: "Tanggal mulai berlakunya kurs ini. Perubahan kurs membuat baris baru." },
  },
  projects: {
    label: "Proyek", desc: "Data master proyek dan referensi pusat biaya.",
    cols: ["code","name","department","project_status","budget_reference"],
  },
  chart_of_accounts: {
    label: "Daftar Akun (COA)", desc: "Bagan akun akuntansi perusahaan. Akun terpakai tidak dapat dihapus.",
    cols: ["code","name","category","normal_balance","allow_direct_posting"],
    tooltips: {
      normal_balance: "Menentukan posisi normal saldo akun dalam pembukuan.",
      allow_direct_posting: "Jika dimatikan, akun ini hanya berlaku sebagai akun induk (parent).",
      parent_code: "Kode akun induk. Kosongkan bila ini adalah akun tingkat atas.",
    },
  },
  taxes: {
    label: "Pajak", desc: "Konfigurasi tarif pajak. Perubahan tarif membuat konfigurasi baru sesuai tanggal berlaku.",
    cols: ["code","name","tax_type","rate","effective_from"],
    tooltips: {
      effective_from: "Tanggal mulai berlakunya konfigurasi pajak ini. Data historis tidak diubah.",
      rate: "Tarif dalam persen (%).",
    },
  },
  document_numbering: {
    label: "Penomoran Dokumen", desc: "Format nomor dokumen otomatis untuk transaksi masa depan.",
    cols: ["doc_type","prefix","company_code","separator","example"],
    tooltips: {
      current_sequence: "Nomor urut terakhir yang telah digunakan. Sistem akan meneruskan dari nilai ini.",
      example: "Contoh format nomor dokumen yang akan dihasilkan.",
    },
  },
  opening_balances: {
    label: "Saldo Awal", desc: "Migrasi saldo awal periode pembukuan. Belum diposting hingga Accounting Engine aktif.",
    cols: ["category","reference_code","reference_name","date","amount","post_status"],
    tooltips: { date: "Tanggal saldo awal, biasanya tanggal migrasi sistem." },
  },
};

export function MasterPage({ masterKey }) {
  const navigate = useNavigate();
  const cfg = CONFIG[masterKey];
  if (!cfg) return <div>Modul tidak ditemukan.</div>;
  return (
    <div className="space-y-6" data-testid={`master-page-${masterKey}`}>
      <Button variant="ghost" size="sm" onClick={() => navigate("/data-master")} className="text-slate-500 -ml-2" data-testid="back-to-hub">
        <ChevronLeft className="h-4 w-4 mr-1" /> Data Master
      </Button>
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{cfg.label}</h1>
        <p className="text-slate-500 mt-2">{cfg.desc}</p>
      </div>
      <MasterCrud masterKey={masterKey} primaryColumns={cfg.cols} tooltips={cfg.tooltips || {}} formSections={cfg.sections || null} />
    </div>
  );
}
